import { useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Popover,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MdStar as StarIcon, MdArrowBack as BackIcon } from 'react-icons/md';
import { useTagGroups } from '@/lib/recipeService';
import { FILTER_EXCLUDED_TAG_GROUPS } from '@/constants/tags';

const RATING_OPTIONS = [
  { label: '2+', value: 2 },
  { label: '3+', value: 3 },
  { label: '4+', value: 4 },
  { label: '5★', value: 5 },
];
const GROUP_COLORS: Record<string, string> = {
  Rating: '#c85a1e',
  Cuisine: '#366b7d',
  Diet:    '#729e6b',
  Meal:    '#b4adb2',
  Course:  '#d97706',
};
const DEFAULT_GROUP_COLOR = '#8a7f76';
function groupColor(name: string) { return GROUP_COLORS[name] ?? DEFAULT_GROUP_COLOR; }

type Step = 'closed' | 'category' | string; // string = group name (value picker)

interface AddFilterButtonProps {
  selectedTags: string[];
  onToggleTag: (tag: string, group: string) => void;
  selectedRatings: number[];
  onToggleRating: (value: number) => void;
}

export default function AddFilterButton({
  selectedTags,
  onToggleTag,
  selectedRatings,
  onToggleRating,
}: AddFilterButtonProps) {
  const [step, setStep] = useState<Step>('closed');
  const isOpen = step !== 'closed';

  const { tagGroups, isLoading } = useTagGroups({ exclude: FILTER_EXCLUDED_TAG_GROUPS });

  const close = () => setStep('closed');
  const openCategory = () => setStep('category');
  const selectGroup = (name: string) => setStep(name);

  const renderHeader = (title: string, color?: string, onBack?: () => void) => (
    <Flex align="center" gap={2} px={4} py="10px" borderBottom="1px solid" borderColor="border.muted" flexShrink={0}>
      {onBack && (
        <Box as="button" onClick={onBack} color="text.muted" display="flex" alignItems="center" cursor="pointer" mr={1} _hover={{ color: 'text.primary' }}>
          <Icon as={BackIcon} boxSize="16px" />
        </Box>
      )}
      {color && <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} />}
      <Text fontFamily="heading" fontWeight="700" fontSize="10px" letterSpacing="0.1em" textTransform="uppercase" color="text.muted">
        {title}
      </Text>
    </Flex>
  );

  const renderCategoryList = () => {
    const allGroups = [{ name: 'Rating', tags: [] as string[] }, ...tagGroups];
    return (
      <Flex direction="column">
        {isLoading ? (
          <Flex justify="center" py={4}><Spinner size="sm" color="accent.secondary" /></Flex>
        ) : (
          allGroups.map((g) => (
            <Flex key={g.name} as="button" align="center" gap={3} px={4} py="10px" cursor="pointer" onClick={() => selectGroup(g.name)} _hover={{ bg: 'bg.surface' }} borderBottom="1px solid" borderColor="border.muted" _last={{ borderBottom: 'none' }} textAlign="left">
              <Box w="6px" h="6px" borderRadius="full" bg={groupColor(g.name)} flexShrink={0} />
              <Text fontFamily="body" fontSize="13px" color="text.primary">{g.name}</Text>
              <Box ml="auto" color="text.muted" fontSize="14px" lineHeight="1">›</Box>
            </Flex>
          ))
        )}
      </Flex>
    );
  };

  const renderValueList = (group: string) => {
    if (group === 'Rating') {
      return (
        <Flex direction="column">
          {RATING_OPTIONS.map(({ label, value }) => {
            const active = selectedRatings.includes(value);
            return (
              <Flex key={value} as="button" align="center" gap={3} px={4} py="10px" cursor="pointer" onClick={() => onToggleRating(value)} bg={active ? 'accent.secondarySubtle' : 'transparent'} _hover={{ bg: active ? 'accent.secondarySubtle' : 'bg.surface' }} borderBottom="1px solid" borderColor="border.muted" _last={{ borderBottom: 'none' }} textAlign="left">
                <Box w="15px" h="15px" borderRadius="4px" border="1.5px solid" bg={active ? '#c85a1e' : 'transparent'} borderColor={active ? '#c85a1e' : 'border.muted'} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  {active && <Box w="7px" h="7px" borderRadius="2px" bg="white" />}
                </Box>
                <Flex align="center" gap="4px">
                  <Icon as={StarIcon} boxSize="12px" color="text.muted" />
                  <Text fontFamily="body" fontSize="13px" color="text.primary">{label}</Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      );
    }
    const tagGroup = tagGroups.find((g) => g.name === group);
    if (!tagGroup) return null;
    const color = groupColor(group);
    return (
      <Flex direction="column" maxH="260px" overflowY="auto">
        {tagGroup.tags.map((tag) => {
          const active = selectedTags.includes(tag);
          return (
            <Flex key={tag} as="button" align="center" gap={3} px={4} py="10px" cursor="pointer" onClick={() => onToggleTag(tag, group)} bg={active ? 'accent.secondarySubtle' : 'transparent'} _hover={{ bg: active ? 'accent.secondarySubtle' : 'bg.surface' }} borderBottom="1px solid" borderColor="border.muted" _last={{ borderBottom: 'none' }} textAlign="left">
              <Box w="15px" h="15px" borderRadius="4px" border="1.5px solid" bg={active ? color : 'transparent'} borderColor={active ? color : 'border.muted'} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                {active && <Box w="7px" h="7px" borderRadius="2px" bg="white" />}
              </Box>
              <Text fontFamily="body" fontSize="13px" color="text.primary">{tag}</Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) close(); }}
      positioning={{ placement: 'bottom-end' }}
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger asChild>
        <Box
          as="button"
          onClick={() => (isOpen ? close() : openCategory())}
          h="30px"
          px={3}
          borderRadius="8px"
          border="1.5px dashed"
          borderColor="accent.secondary"
          bg={isOpen ? 'accent.secondarySubtle' : 'transparent'}
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          fontSize="12px"
          fontWeight="600"
          fontFamily="body"
          color="accent.secondary"
          cursor="pointer"
          whiteSpace="nowrap"
          flexShrink={0}
          _hover={{ bg: 'accent.secondarySubtle' }}
          transition="all 0.12s"
        >
          + Add filter
        </Box>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content p={0} minW="200px" overflow="hidden">
            {step === 'category' && (
              <>
                {renderHeader('Add a filter')}
                {renderCategoryList()}
              </>
            )}
            {step !== 'category' && step !== 'closed' && (
              <>
                {renderHeader(step, groupColor(step), () => setStep('category'))}
                {renderValueList(step)}
              </>
            )}
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
